import { instanceBo } from "../utils/axios.create";
import {
	ActionGroupStatus,
	IActionGroup,
	RolesPreviewSection,
} from "../domain/action-group.interface";

export interface ActionGroupRequest {
	name: string;
	description?: string;
	icon?: string;
	order?: number;
	status?: ActionGroupStatus;
	createdBy: string;
}

export interface ActionGroupUpdateRequest {
	_id: string;
	name?: string;
	description?: string;
	icon?: string;
	order?: number;
	status?: ActionGroupStatus;
	updatedBy: string;
}

export interface ActionGroupReorderItem {
	groupId: string;
	order: number;
}

export interface ActionGroupsResponse {
	statusCode: number | string;
	body: {
		data: IActionGroup[];
		pagination: {
			currentPage: number;
			pageSize: number;
			totalCount: number;
			totalPages: number;
		};
	};
}

/**
 * Page size used to pull the whole group catalog in one request, for the
 * selectors that need every group (create/edit action, reorder draft).
 * Groups are a small, hand-curated catalog — a handful per account — so one
 * page is enough; `getAllActionGroups` warns if that ever stops holding.
 */
export const ACTION_GROUPS_MAX_PAGE_SIZE = 1000;

export const getActionGroups = async (
	search?: string,
	pageNumber?: number,
	pageSize?: number,
	status?: ActionGroupStatus,
): Promise<ActionGroupsResponse> => {
	const params = new URLSearchParams();
	if (search) params.append("search", search);
	// Explicit 0 is a caller-supplied value and must be forwarded, so these
	// guard on undefined rather than on truthiness.
	if (pageNumber !== undefined)
		params.append("pageNumber", pageNumber.toString());
	if (pageSize !== undefined) params.append("pageSize", pageSize.toString());
	if (status) params.append("status", status);

	const queryString = params.toString();
	const url = `/action-groups${queryString ? `?${queryString}` : ""}`;

	return instanceBo
		.get(url)
		.then((res) => res.data)
		.catch((err) => Promise.reject(err.response?.data || err));
};

/**
 * Fetch the whole group catalog in ONE page of `ACTION_GROUPS_MAX_PAGE_SIZE`.
 *
 * TRUNCATION IS POSSIBLE, BY DESIGN. An account with more groups than that cap
 * gets a short list back rather than an error, because every caller — the group
 * selector, the reorder draft, the FR-13 gate — is better off rendering most of
 * the catalog than failing the whole Actions screen. The overflow is reported on
 * the console outside production builds; check `body.pagination.totalCount`
 * against the returned `body.data.length` if a caller needs to detect it.
 */
export const getAllActionGroups = async (): Promise<ActionGroupsResponse> => {
	const response = await getActionGroups(
		undefined,
		1,
		ACTION_GROUPS_MAX_PAGE_SIZE,
	);
	// Single-page fetch: say so rather than silently returning a short list, so
	// a truncated group selector is diagnosable instead of looking like data loss.
	// Kept out of production builds — a console line there reaches nobody on call
	// and only clutters the user's devtools.
	const { totalCount } = response?.body?.pagination ?? {};
	if (
		process.env.NODE_ENV !== "production" &&
		totalCount !== undefined &&
		totalCount > ACTION_GROUPS_MAX_PAGE_SIZE
	) {
		// eslint-disable-next-line no-console
		console.warn(
			`getAllActionGroups: action groups truncated — ${totalCount} exist but only ${ACTION_GROUPS_MAX_PAGE_SIZE} were fetched.`,
		);
	}
	return response;
};

export const createActionGroup = async (
	data: ActionGroupRequest,
): Promise<IActionGroup> => {
	return instanceBo
		.post("/action-groups", data)
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response?.data || err));
};

export const updateActionGroup = async (
	data: ActionGroupUpdateRequest,
): Promise<IActionGroup> => {
	const { _id, ...body } = data;
	return instanceBo
		.put(`/action-groups/${_id}`, body)
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response?.data || err));
};

export const deleteActionGroup = async (groupId: string): Promise<void> => {
	return instanceBo
		.delete(`/action-groups/${groupId}`)
		.then(() => undefined)
		.catch((err) => Promise.reject(err.response?.data || err));
};

export const reorderActionGroups = async (
	items: ActionGroupReorderItem[],
	updatedBy: string,
): Promise<IActionGroup[]> => {
	return instanceBo
		.put("/action-groups/reorder", { items, updatedBy })
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response?.data || err));
};

export const getRolesPreview = async (): Promise<RolesPreviewSection[]> => {
	return instanceBo
		.get("/action-groups/roles-preview")
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response?.data || err));
};
